// src/common/datetime.js
import { DateTime, Duration } from 'luxon'

/**
 * Convenciones del proyecto:
 * - Las fechas de API llegan como ISO-8601 en UTC (terminan en 'Z').
 * - Para mostrar localmente, pasa una zona IANA válida (p.ej. 'Europe/Madrid').
 * - Los cálculos de duración/delay se hacen en UTC.
 */

const DEFAULT_LOCALE = 'en-EN'
const FALLBACK_TZ = 'UTC'

// ---------------------------
// Parsing / Conversions
// ---------------------------
export function parseUtc(isoUtc) {
  const dt = DateTime.fromISO(isoUtc, { zone: 'utc' })
  if (!dt.isValid) throw new Error(`Fecha inválida: ${isoUtc} (${dt.invalidReason})`)
  return dt
}

export function toTz(isoUtc, tz) {
  return parseUtc(isoUtc).setZone(tz || FALLBACK_TZ)
}

export function toEpochMs(isoUtc) {
  return parseUtc(isoUtc).toMillis()
}

// ---------------------------
// Formatting (display)
// ---------------------------
/** Corto local (listas/tablas): '04 oct 15:20' */
export function formatShortLocal(isoUtc, tz) {
  return toTz(isoUtc, tz).setLocale(DEFAULT_LOCALE).toFormat('dd LLL yyyy, HH:mm')
}

/** Largo local con zona (detalle): 'sábado, 04 oct 2025 · 15:20 h (Europe/Madrid)' */
export function formatLongLocalWithZone(isoUtc, tz) {
  const dt = toTz(isoUtc, tz).setLocale(DEFAULT_LOCALE)
  return `${dt.toFormat('cccc, dd LLL yyyy · HH:mm')} h (${dt.zoneName})`
}

/** UTC compacto: '2025-10-04 13:20 UTC' */
export function formatUTC(isoUtc) {
  return parseUtc(isoUtc).toFormat("yyyy-LL-dd HH:mm 'UTC'")
}

/** Solo fecha local: '04 oct 2025' */
export function formatDateLocal(isoUtc, tz) {
  return toTz(isoUtc, tz).setLocale(DEFAULT_LOCALE).toFormat('dd LLL, yyyy')
}

/** Solo hora local 24h: '15:20' */
export function formatTimeLocal(isoUtc, tz) {
  return toTz(isoUtc, tz).toFormat('HH:mm')
}

/** Rango local con duración: '04 oct 15:20 → 04 oct 18:05 (2h 45m)' */
export function formatRangeLocal(startUtc, endUtc, tz) {
  const a = toTz(startUtc, tz).setLocale(DEFAULT_LOCALE)
  const b = toTz(endUtc, tz).setLocale(DEFAULT_LOCALE)
  const left = a.toFormat('dd LLL HH:mm')
  const right = b.toFormat('dd LLL HH:mm')
  const dur = Duration.fromMillis(Math.max(0, b.toMillis() - a.toMillis()))
  return `${left} → ${right} (${formatDuration(dur)})`
}

/** Relativo (desde ahora) en tz local: 'hace 12 min', 'en 2 h' */
export function formatRelative(isoUtc, tz) {
  const now = DateTime.now().setZone(tz)
  const dt = toTz(isoUtc, tz)
  return dt.toRelative({ base: now, locale: DEFAULT_LOCALE }) ?? ''
}

// ---------------------------
// Durations / Delays
// ---------------------------
/** Duración entre dos ISO UTC como 'Hh Mm' */
export function formatDurationBetween(startUtc, endUtc) {
  const ms = Math.max(0, toEpochMs(endUtc) - toEpochMs(startUtc))
  return formatDuration(Duration.fromMillis(ms))
}

/** Duración desde un Duration Luxon -> 'Hh Mm' */
export function formatDuration(dur) {
  const shifted = dur.shiftTo('hours', 'minutes').toObject()
  const h = Math.trunc(shifted.hours || 0)
  const m = Math.trunc(shifted.minutes || 0)
  return `${h}h ${m}m`
}

/** Delay en minutos (AT - ST) en UTC -> '+23 min' | '0 min' */
export function formatDelayMinutes(schedUtc, actualUtc) {
  const diff = Math.round((toEpochMs(actualUtc) - toEpochMs(schedUtc)) / 60000)
  const sign = diff > 0 ? '+'
    : '' // si quieres distinguir adelantos, podrías usar '-' cuando diff < 0
  return `${sign}${diff} min`
}

// ---------------------------
// Safe guards / utils
// ---------------------------

export function tryFormat(fn, fallback = '—') {
  return (...args) => {
    try {
      return fn(...args)
    } catch {
      return fallback
    }
  }
}

export function hasRealTime(isoUtc) {
  if (!isoUtc || typeof isoUtc !== 'string') return false
  // Evita confiar solo en regex por si viene con ms; valida con Luxon
  const dt = parseUtc(isoUtc) // UTC
  return dt.hour !== 0 || dt.minute !== 0 || dt.second !== 0 || dt.millisecond !== 0
}

export function formatSmartLocal(isoUtc, tz) {
  if (!isoUtc) return '—'
  return hasRealTime(isoUtc)
    ? formatShortLocal(isoUtc, tz)
    : formatDateLocal(isoUtc, tz)
}

export function isNearExpiration(isoUtc, { days = 7, tz = 'UTC', inclusive = true } = {}) {
  if (daysUntil(isoUtc, tz) === null) return false
  return daysUntil(isoUtc, tz) <= days && (inclusive ? daysUntil(isoUtc, tz) >= 0 : daysUntil(isoUtc, tz) > 0)
}

export function daysUntil(isoUtc, tz = 'UTC') {
  if (!isoUtc) return null
  const now = DateTime.now().setZone(tz)
  const exp = toTz(isoUtc, tz)
  return Math.ceil(exp.diff(now, 'days').days)
}

export function formatDateTime(isoUtc, { tz = 'UTC', pattern = 'yyyy-LL-dd HH:mm', locale = DEFAULT_LOCALE } = {}) {
  if (!isoUtc) return '—'
  return toTz(isoUtc, tz).setLocale(locale).toFormat(pattern)
}

//
// export const safe = {
//   shortLocal: tryFormat(formatShortLocal),
//   longLocal: tryFormat(formatLongLocalWithZone),
//   utc: tryFormat(formatUTC),
//   utcISO: tryFormat(formatUtcISO),
//   dateLocal: tryFormat(formatDateLocal),
//   timeLocal: tryFormat(formatTimeLocal),
//   rangeLocal: tryFormat(formatRangeLocal),
//   relative: tryFormat(formatRelative),
//   durationBetween: tryFormat(formatDurationBetween),
//   delayMinutes: tryFormat(formatDelayMinutes),
//   opsLabel: tryFormat(formatOpsLabel),
//   auditLine: tryFormat(formatAuditLine),
// }